import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";
import { Knob } from "primereact/knob";
import { Checkbox } from "primereact/checkbox";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressBar } from "primereact/progressbar";
import { Badge } from "primereact/badge";

import MonthDropdown from "../../../../components/inputs/MonthDropdown";
import { previousMonthPeriod } from "../../../../utils/previousMonth";
import YearDropdown from "../../../../components/inputs/YearDropdown";
import NumberInputField from "../../../../components/inputs/NumberInputField";
import { InputLabel } from "../../../../components/inputs/InputLabel";
import { ResultsDisplayHeader } from "./components/ResultsDisplayHeader";
import { FinalizeDialog, easybillDocumentUrl } from "./components/FinalizeDialog";
import type { ValidationInfo } from "./components/FinalizeDialog";
import { WalletCreditByOwner } from "./components/WalletCreditByOwner";
import { EroundPrefillHint } from "./components/EroundPrefillHint";

import {
  clearingValidationByMonth,
  clearingValidateCreditNoteVat,
} from "../../../../services/clearing/validation";
import { isProduction } from "../../../../utils/environment";
import { useEroundPrefill } from "./hooks/useEroundPrefill";

type CreditNotesValidationResult = {
  passed: boolean;
  sumIn: number;
  directPayStripeSum: number;
  paymentStripeSum: number;
  easyBillCreditNotesSum: number;
  errorRate: number;
  difference: number;
  month: number;
  year: number;
  message: string;
  creditNotes: ValidateableCreditNote[];
  // PAY-11 / CLR-150 — additive wallet-credit fields. Defaults applied at the
  // service-layer normalisation boundary in services/clearing/validation.js.
  walletCreditSum?: number;
  walletCreditByCustomer?: Record<string, number> | null;
  refundsAcknowledgedV1?: boolean;
};

type ValidateableCreditNote = {
  documentId: number | null;
  title: string | null;
  documentDate: string | null;
  amountNet: number | null;
  amount: number | null;
  vatOption: string | null;
  dueInDays: number | null;
  customerId: number | null;
  serviceDate: object | null;
  isDraft: boolean | null;
  easybillContactId: number | null;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
};

const FINALIZE_DELAY_SECONDS = isProduction ? 5 : 15;

// Validation runs once a month has closed, so default to the previous month.
const initialSelectedPeriod = previousMonthPeriod();

const getDefaultDocumentDatePeriod = (month: number, year: number) => ({
  month: month === 12 ? 1 : month + 1,
  year: month === 12 ? year + 1 : year,
});

const isAfterMarch2026 = (period: { month: number; year: number }) =>
    period.year > 2026 || (period.year === 2026 && period.month >= 3);

const isAfterJanuary2026 = (period: { month: number; year: number }) => period.year >= 2026;

export function CreditNoteValidationForm({ visible, onClose }) {
  const toast = useRef(null);

  const [selectedPeriod, setSelectedPeriod] = useState(initialSelectedPeriod);
  const { eroundSum, setEroundSum, eroundPrefilled } = useEroundPrefill(selectedPeriod);

  const [deletedCreditNotesSum, setDeletedCreditNotesSum] = useState(0);
  const [enforceTitlePrefix, setEnforceTitlePrefix] = useState(isAfterMarch2026(initialSelectedPeriod));
  const [skipWithoutServiceDate, setSkipWithoutServiceDate] = useState(isAfterJanuary2026(initialSelectedPeriod));
  const [documentDatePeriod, setDocumentDatePeriod] = useState(
      getDefaultDocumentDatePeriod(initialSelectedPeriod.month, initialSelectedPeriod.year)
  );

  // The form stays mounted while the dialog is hidden, so a tab left open into a new
  // month would keep the old default. Re-default only when the default itself moved,
  // so a period picked by hand (and its results) survive closing and reopening.
  const defaultPeriodRef = useRef(initialSelectedPeriod);
  useEffect(() => {
    if (!visible) return;
    const defaultPeriod = previousMonthPeriod();
    const lastDefault = defaultPeriodRef.current;
    if (defaultPeriod.month === lastDefault.month && defaultPeriod.year === lastDefault.year) return;
    defaultPeriodRef.current = defaultPeriod;
    setSelectedPeriod(defaultPeriod);
    setEnforceTitlePrefix(isAfterMarch2026(defaultPeriod));
    setSkipWithoutServiceDate(isAfterJanuary2026(defaultPeriod));
    setDocumentDatePeriod(getDefaultDocumentDatePeriod(defaultPeriod.month, defaultPeriod.year));
  }, [visible]);

  const [validationResults, setValidationResults] = useState<CreditNotesValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [vatLoading, setVatLoading] = useState(false);
  const [vatWarnings, setVatWarnings] = useState<ValidationInfo[] | null>(null);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);

  const getValidationResults = useCallback(async () => {
    setLoading(true);
    try {
      const results = await clearingValidationByMonth({
        month: selectedPeriod.month,
        year: selectedPeriod.year,
        eroundSum,
        deletedCreditNotesSum,
        enforceTitlePrefix,
        skipWithoutServiceDate,
        documentDateMonth: documentDatePeriod.month,
        documentDateYear: documentDatePeriod.year,
      });
      setValidationResults(results);
    } catch (err) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: (err as Error)?.message || "Failed to complete clearing validation.",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, eroundSum, deletedCreditNotesSum, enforceTitlePrefix, skipWithoutServiceDate, documentDatePeriod]);

  const handleValidateVat = useCallback(async () => {
    if (!validationResults?.creditNotes?.length) return;

    setVatLoading(true);
    const warnings = await clearingValidateCreditNoteVat({
      creditNotes: validationResults.creditNotes,
      month: validationResults.month,
      year: validationResults.year,
      enforceTitlePrefix,
      skipWithoutServiceDate,
    });

    if (warnings) {
      setVatWarnings(warnings);
      setShowFinalizeDialog(true);
    } else {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to validate VAT.",
      });
    }

    setVatLoading(false);
  }, [validationResults, enforceTitlePrefix, skipWithoutServiceDate]);

  const handleCloseFinalizeDialog = () => {
    setShowFinalizeDialog(false);
    setVatWarnings(null);
  };

  return (
      <>
        <Dialog
            header="Credit Note Validation"
            visible={visible && !showFinalizeDialog}
            style={{ width: "55vw" }}
            onHide={onClose}
        >
          <Toast ref={toast} />

          <section className="flex flex-column">
            <div className="flex flex-column">
              <div className="">
                <InputLabel label={"Service Period"} required />
                <div className="flex flex-column sm:flex-row gap-2 field">
                  <MonthDropdown
                      monthIndex={selectedPeriod.month}
                      onChange={(option) => {
                        const month = Number(option.code);
                        setSelectedPeriod((prev) => ({ ...prev, month }));
                        setDocumentDatePeriod(getDefaultDocumentDatePeriod(month, selectedPeriod.year));
                        setEnforceTitlePrefix(
                            selectedPeriod.year > 2026 ||
                            (selectedPeriod.year === 2026 && month >= 3)
                        );
                        setSkipWithoutServiceDate(selectedPeriod.year >= 2026);
                      }}
                  />
                  <YearDropdown
                      year={selectedPeriod.year}
                      onChange={(option) => {
                        const year = Number(option.code);
                        setSelectedPeriod((prev) => ({ ...prev, year }));
                        setDocumentDatePeriod(getDefaultDocumentDatePeriod(selectedPeriod.month, year));
                        setEnforceTitlePrefix(
                            year > 2026 ||
                            (year === 2026 && selectedPeriod.month >= 3)
                        );
                        setSkipWithoutServiceDate(year >= 2026);
                      }}
                  />
                </div>
              </div>

              <div className="">
                <InputLabel label={"Document Date Range (EasyBill query month)"} />
                <div className="flex flex-column sm:flex-row gap-2 field">
                  <MonthDropdown
                      monthIndex={documentDatePeriod.month}
                      onChange={(option) =>
                          setDocumentDatePeriod((prev) => ({
                            ...prev,
                            month: Number(option.code),
                          }))
                      }
                  />
                  <YearDropdown
                      year={documentDatePeriod.year}
                      onChange={(option) =>
                          setDocumentDatePeriod((prev) => ({
                            ...prev,
                            year: Number(option.code),
                          }))
                      }
                  />
                </div>
              </div>

              <NumberInputField
                  inputClassName="w-1rem sm:w-25rem sm:h-3rem flex"
                  name="eroundSum"
                  label="Eround Total (optional)"
                  value={eroundSum}
                  onChange={(e) => setEroundSum(e.value)}
                  min={0}
                  step={0.01}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="currency"
                  locale="de-DE"
                  showButtons
                  containerClassName="p-0 field"
              />
              <small className="text-color-secondary -mt-2 mb-3 block">
                Leave blank to fetch automatically from eRound; enter a value only to override.
              </small>
              <EroundPrefillHint visible={eroundPrefilled} />

              <NumberInputField
                  inputClassName="w-1rem sm:w-25rem sm:h-3rem flex"
                  name="deletedCreditNotesSum"
                  label="Deleted Credit Notes Total"
                  required
                  value={deletedCreditNotesSum}
                  onChange={(e) => setDeletedCreditNotesSum(Number(e.target.value))}
                  min={0}
                  step={0.01}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="currency"
                  locale="de-DE"
                  showButtons
                  containerClassName="p-0 field"
              />

              <div className="flex flex-column gap-2 field">
                <div className="flex align-items-center gap-2">
                  <Checkbox
                      inputId="enforceTitlePrefix"
                      checked={enforceTitlePrefix}
                      onChange={(e) => setEnforceTitlePrefix(e.checked)}
                  />
                  <label htmlFor="enforceTitlePrefix">
                    Only validate credit notes with the prefix CN-YYMM- (recommended from March 2026)
                  </label>
                </div>
                <div className="flex align-items-center gap-2">
                  <Checkbox
                      inputId="skipWithoutServiceDate"
                      checked={skipWithoutServiceDate}
                      onChange={(e) => setSkipWithoutServiceDate(e.checked)}
                  />
                  <label htmlFor="skipWithoutServiceDate">
                    Do not validate credit notes without ServiceDate (recommended from January 2026)
                  </label>
                </div>
              </div>
            </div>

            <Button
                className="w-16rem sm:h-3rem sm:mt-2"
                style={{ alignSelf: "end" }}
                label="Validate Credit Notes"
                icon="pi pi-check-circle"
                onClick={getValidationResults}
                loading={loading}
            />
          </section>

          {loading && (
              <div className="mt-3">
                <p className="text-center text-sm text-color-secondary mb-2">Validating credit notes...</p>
                <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
              </div>
          )}

          {validationResults && (
              <CreditNoteResults
                  results={validationResults}
                  onValidateVat={handleValidateVat}
                  vatLoading={vatLoading}
              />
          )}
        </Dialog>

        {showFinalizeDialog && vatWarnings && (
            <FinalizeDialog
                visible={showFinalizeDialog}
                warnings={vatWarnings}
                onClose={handleCloseFinalizeDialog}
                header="Finalize and Send Credit Notes"
                buttonLabel="Finalize and Send Validated Credit Notes"
                successMessage="Finalized all credit notes successfully."
                delaySeconds={FINALIZE_DELAY_SECONDS}
            />
        )}
      </>
  );
}

type DuplicateGroup = {
  title: string;
  documentIds: number[];
};

function findDuplicates(creditNotes: ValidateableCreditNote[]): DuplicateGroup[] {
  const grouped = new Map<string, number[]>();
  for (const note of creditNotes) {
    if (!note.title) continue;
    const ids = grouped.get(note.title);
    if (ids) {
      ids.push(note.documentId!);
    } else {
      grouped.set(note.title, [note.documentId!]);
    }
  }
  const duplicates: DuplicateGroup[] = [];
  for (const [title, documentIds] of grouped) {
    if (documentIds.length > 1) {
      duplicates.push({ title, documentIds });
    }
  }
  return duplicates;
}

function CreditNoteResults({
                             results,
                             onValidateVat,
                             vatLoading,
                           }: {
  results: CreditNotesValidationResult;
  onValidateVat: () => void;
  vatLoading: boolean;
}) {
  const knobColor = results.passed ? "#22C55E" : "#EF4444";
  const vatEnabled = results.errorRate < 1;
  const [showDuplicatesDialog, setShowDuplicatesDialog] = useState(false);

  const duplicates = useMemo(
      () => findDuplicates(results.creditNotes ?? []),
      [results.creditNotes]
  );

  const totalDuplicateNotes = duplicates.reduce((sum, g) => sum + g.documentIds.length, 0);

  return (
      <section>
        <Divider />

        <ResultsDisplayHeader monthIndex={results.month} year={results.year} />

        <section className="my-4 flex flex-row justify-content-around align-items-center">
          <div className="flex flex-column align-items-center">
            <Knob
                value={Math.round(results.errorRate * 100) / 100}
                valueColor={knobColor}
                readOnly
                size={120}
                max={100}
                valueTemplate={`${(Math.round(results.errorRate * 100) / 100).toFixed(2)}%`}
            />
            <span
                className="mt-2 font-bold"
                style={{ color: knobColor }}
            >
            {results.passed ? "PASSED" : "FAILED"}
          </span>
          </div>

          <div className="flex flex-column gap-2">
            <div className="flex justify-content-between gap-4">
              <span className="font-semibold">Sum In (Eround + DPay + Payment + Wallet):</span>
              <span>
              {results.sumIn?.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
            </span>
            </div>
            <div className="flex justify-content-between gap-4">
              <span className="font-semibold">Direct Pay Stripe Sum:</span>
              <span>
              {results.directPayStripeSum?.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
            </span>
            </div>
            <div className="flex justify-content-between gap-4">
              <span className="font-semibold">Payment Stripe Sum:</span>
              <span>
              {results.paymentStripeSum?.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
            </span>
            </div>
            <div className="flex justify-content-between gap-4">
              <span className="font-semibold">Wallet credit:</span>
              <span>
              {(results.walletCreditSum ?? 0).toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
            </span>
            </div>
            <div className="flex justify-content-between gap-4">
              <span className="font-semibold">EasyBill Credit Notes Sum:</span>
              <span>
              {results.easyBillCreditNotesSum?.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
            </span>
            </div>
            <Divider className="my-1" />
            <div className="flex justify-content-between gap-4">
              <span className="font-semibold">Difference:</span>
              <span>
              {results.difference?.toLocaleString("de-DE", {
                style: "currency",
                currency: "EUR",
              })}
            </span>
            </div>
            <div className="flex justify-content-between align-items-center gap-4">
              <span className="font-semibold">Number of Credit Notes:</span>
              <span className="flex align-items-center gap-2">
              <span className="font-bold text-lg">{results.creditNotes?.length ?? 0}</span>
                {duplicates.length > 0 && (
                    <Button
                        label="Show Duplicates"
                        icon="pi pi-exclamation-triangle"
                        className="p-button-outlined p-button-warning p-button-sm"
                        badge={String(totalDuplicateNotes)}
                        onClick={() => setShowDuplicatesDialog(true)}
                    />
                )}
            </span>
            </div>
            <div className="flex justify-content-between gap-4">
              <span className="font-semibold">Message:</span>
              <span>{results.message}</span>
            </div>
          </div>
        </section>

        <div className="flex justify-content-end mt-3">
          <Button
              label="Validate VAT"
              icon="pi pi-verified"
              onClick={onValidateVat}
              loading={vatLoading}
              disabled={!vatEnabled}
              tooltip={!vatEnabled ? "Error rate must be under 1% to validate VAT" : undefined}
              tooltipOptions={{ position: "left" }}
          />
        </div>

        {vatLoading && (
            <div className="mt-3">
              <p className="text-center text-sm text-color-secondary mb-2">Validating VAT options...</p>
              <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
            </div>
        )}

        <WalletCreditByOwner
            walletCreditByCustomer={results.walletCreditByCustomer}
            refundsAcknowledgedV1={results.refundsAcknowledgedV1}
        />

        <Dialog
            header="Duplicate Credit Notes"
            visible={showDuplicatesDialog}
            style={{ width: "50vw" }}
            onHide={() => setShowDuplicatesDialog(false)}
        >
          <p className="mb-3 text-color-secondary">
            {duplicates.length} title{duplicates.length !== 1 ? "s" : ""} with {totalDuplicateNotes} total credit notes sharing the same title.
          </p>
          <DataTable value={duplicates} size="small" scrollable scrollHeight="60vh">
            <Column field="title" header="Title" sortable />
            <Column
                header="Count"
                sortable
                sortField="documentIds"
                body={(row: DuplicateGroup) => (
                    <Badge value={String(row.documentIds.length)} severity="danger" />
                )}
                style={{ width: "5rem", textAlign: "center" }}
            />
            <Column
                header="Document IDs"
                body={(row: DuplicateGroup) => (
                    <div className="flex flex-wrap gap-1">
                      {row.documentIds.map((id) => (
                          <a
                              key={id}
                              href={easybillDocumentUrl(id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="border-round px-2 py-1 text-sm no-underline"
                              style={{ background: "#f1f5f9", border: "1px solid #cbd5e1", color: "#2563eb" }}
                          >
                    {id}
                  </a>
                      ))}
                    </div>
                )}
            />
          </DataTable>
        </Dialog>
      </section>
  );
}

